import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

type JobRow = {
  id: string;
  user_id: string | null;
  category_set_id: string | null;
  input_data: any;
  total_count?: number | null;
  result?: any | null;
  status: 'queued' | 'processing' | 'done' | 'failed' | string;
  created_at: string | null;
  updated_at: string | null;
};

const statusChipStyle = (status: string): React.CSSProperties => {
  const s = (status || '').toLowerCase();
  if (s === 'done') {
    return { background: 'rgba(46,204,113,0.15)', border: '1px solid rgba(46,204,113,0.45)', color: '#9be564', padding: '4px 8px', borderRadius: 999 };
  }
  if (s === 'failed') {
    return { background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.45)', color: '#ff6b6b', padding: '4px 8px', borderRadius: 999 };
  }
  if (s === 'queued') {
    return { background: 'rgba(233,235,158,0.20)', border: '1px solid rgba(233,235,158,0.55)', color: '#E9EB9E', padding: '4px 8px', borderRadius: 999 };
  }
  // processing or unknown
  return { background: 'rgba(84,101,255,0.15)', border: '1px solid rgba(84,101,255,0.45)', color: '#788BFF', padding: '4px 8px', borderRadius: 999 };
};

const CategoryJobs: React.FC = () => {
  const [jobs, setJobs] = useState<JobRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (userErr) throw userErr;
        const userId = userData.user?.id;
        if (!userId) {
          setError('Please log in to view your jobs.');
          setLoading(false);
          return;
        }
        const { data, error } = await supabase
          .from('jobs')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (!mounted) return;
        setJobs(data || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load jobs');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const getItemsCount = (job: JobRow): number => {
    if (job.total_count != null) return job.total_count || 0;
    const input = job.input_data;
    if (Array.isArray(input)) return input.length;
    if (input && Array.isArray(input.data)) return input.data.length;
    return 0;
  };

  if (loading) return <div>Loading jobs…</div>;
  if (error) return <div style={{ color: '#ff6b6b' }}>{error}</div>;

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Category Jobs</h3>
      <p style={{ color: 'var(--color-text-muted)' }}>
        Track and manage categorization jobs, monitor status, and review results.
      </p>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0f111a' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Job ID</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Items</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {(jobs || []).map(j => (
              <tr key={j.id}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{j.id}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{getItemsCount(j)}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>
                  <span style={statusChipStyle(j.status)}>{j.status}</span>
                </td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{j.created_at ? new Date(j.created_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {(!jobs || jobs.length === 0) && (
              <tr>
                <td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text-muted)' }}>No jobs yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CategoryJobs; 